import { Dispatch, SetStateAction, useState } from "react";
import { useRecoilState } from "recoil";
import moment from "moment";
import { myTimesheetDashboardDateState } from "my-timesheet/states";
import { PeriodType } from "my-timesheet/types/period-type";

export interface UseMyTimesheetDashboardDateResult {
  currentDate: Date;
  period: PeriodType;
  handlePeriodChange: (period: PeriodType) => void;
  handleChangeCurrentDate: (date: Date) => void;
  periodStart: Date;
  periodEnd: Date;
}

/**
 * Manages the dashboard's current date and period (day/week/month).
 *
 * @param id - The unique identifier for the dashboard's date state in Recoil.
 */
export const useMyTimesheetDashboardDate = (
  id: string,
): UseMyTimesheetDashboardDateResult => {
  const [currentDate, setCurrentDate] = useRecoilState<Date>(
    myTimesheetDashboardDateState(id),
  );
  const [period, setPeriod]: [
    PeriodType,
    Dispatch<SetStateAction<PeriodType>>,
  ] = useState<PeriodType>("month");

  // Calculate the start/end of the current period
  const periodStart = moment(currentDate)
    .startOf(period as "day" | "week" | "month")
    .toDate();
  const periodEnd = moment(currentDate)
    .endOf(period as "day" | "week" | "month")
    .toDate();

  const handlePeriodChange = (newPeriod: PeriodType): void => {
    setPeriod(newPeriod);
  };

  const handleChangeCurrentDate = (date: Date): void => {
    setCurrentDate(date);
  };

  return {
    currentDate,
    period,
    handlePeriodChange,
    handleChangeCurrentDate,
    periodStart,
    periodEnd,
  };
};

export default useMyTimesheetDashboardDate;
